import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { VideoOff } from "lucide-react";
import Daily, { type DailyCall } from "@daily-co/daily-js";
import { mintClinicianMeetingToken } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import { hiddenWhenReadOnly } from "./SupportView";

// CAR-52 — the clinician joins the Daily call embedded in the portal instead
// of a window.open'd tab. Embedding means the SPA owns the call object, so it
// can run a token-refresh loop (mirroring the mobile MeetingRoomScreen) — the
// owner token can be short-lived, and the meeting token never rides in a URL
// query param where it could leak via history / referrer / proxy logs.
//
// TES-506 — the room is a PANE, not a full-screen overlay.
//
// The walkthrough 2026-09-04:
// "Se till att man kan se kundinformationen och dokumentationsgrejerna under mötet."
// It was `fixed inset-0 z-50`, so the
// moment the doctor joined, the patient's history and the documentation form
// she was supposed to be filling in were behind the video. The call object,
// the token refresh and the teardown are unchanged; only the box it lives in
// is. The parent decides how much of the screen it gets — see MeetingPrep.

interface Props {
  meetingId: string;
  /** Shown in the pane header, e.g. the patient shorthand. */
  label?: string;
  /** Daily's own leave button — the doctor hung up, the parent leaves. The
   *  visible "Lämna mötet" lives in the screen header, not in this card. */
  onClose: () => void;
  /** Tear the room down and join again — the parent remounts us. */
  onRetry?: () => void;
  /** Where the patient can be reached in text while video is down. */
  chatHref?: string;
}

type Phase = "connecting" | "joined" | "error";

// Re-mint ~5 min before the server-reported expiry. If we're already inside
// that window (or past it) fire immediately so we at least try.
function refreshDelayMs(expiresAt: string): number {
  const expiry = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiry)) return 0;
  return Math.max(0, expiry - Date.now() - 5 * 60 * 1000);
}

function MeetingRoomCall({
  meetingId,
  label,
  onClose,
  onRetry,
  chatHref,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<DailyCall | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `onClose` is typically an inline arrow in the parent — keep it in a ref so
  // the join effect depends only on the meeting id and never re-runs (which
  // would tear down and rebuild the iframe).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [phase, setPhase] = useState<Phase>("connecting");
  const [error, setError] = useState<string | null>(null);

  const clearTimer = () => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  };

  // Re-mint the token, hand it to the live call object, then reschedule from
  // the new expiry so the chain keeps going. Best-effort: a failed refresh is
  // not fatal — the current token is valid until its own expiry — so we never
  // tear the call down on a refresh error.
  const scheduleRefresh = useCallback(
    (expiresAt: string) => {
      clearTimer();
      refreshTimer.current = setTimeout(() => {
        void (async () => {
          try {
            const next = await mintClinicianMeetingToken(meetingId);
            const frame = frameRef.current;
            if (!frame || frame.isDestroyed()) return;
            // `setMeetingToken` isn't in the daily-js 0.84 type surface but is
            // present at runtime — same defensive cast the mobile
            // MeetingRoomScreen uses. If it's somehow absent we just let the
            // current token ride to its own expiry (best-effort).
            const frameAny = frame as unknown as {
              setMeetingToken?: (token: string) => Promise<unknown>;
            };
            if (typeof frameAny.setMeetingToken === "function") {
              await frameAny.setMeetingToken(next.token);
            }
            scheduleRefresh(next.expiresAt);
          } catch {
            // swallow — see comment above
          }
        })();
      }, refreshDelayMs(expiresAt));
    },
    [meetingId],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { roomUrl, token, expiresAt } =
          await mintClinicianMeetingToken(meetingId);
        if (cancelled || !containerRef.current) return;
        const frame = Daily.createFrame(containerRef.current, {
          showLeaveButton: true,
          iframeStyle: { width: "100%", height: "100%", border: "none" },
        });
        frameRef.current = frame;
        frame.on("joined-meeting", () => setPhase("joined"));
        // Only a leave from the live, healthy frame is the doctor hanging
        // up (TES-978: that opens the "end the meeting?" dialog). The
        // teardown below clears `frameRef` before it calls leave(), so a
        // retry or an unmount does not count, and neither does the leave
        // that follows a fatal error — the error card is the state to show
        // then, not a question about ending the meeting.
        let failed = false;
        frame.on("left-meeting", () => {
          if (frameRef.current === frame && !failed) onCloseRef.current();
        });
        frame.on("error", (e) => {
          failed = true;
          setError(e?.errorMsg ?? "Daily-fel");
          setPhase("error");
        });
        await frame.join({ url: roomUrl, token, userName: "Vårdgivare" });
        if (cancelled) return;
        scheduleRefresh(expiresAt);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setPhase("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimer();
      const frame = frameRef.current;
      frameRef.current = null;
      if (frame && !frame.isDestroyed()) {
        void frame
          .leave()
          .catch(() => undefined)
          .finally(() => void frame.destroy());
      }
    };
  }, [meetingId, scheduleRefresh]);

  return (
    <div className="flex h-full min-h-[380px] flex-col overflow-hidden rounded-lg bg-[radial-gradient(120%_80%_at_50%_0%,#5a1d58_0%,#2a1128_60%,#1d0b1c_100%)] text-white shadow-[0_24px_48px_-24px_rgba(42,26,40,0.6)]">
      <div className="flex items-center justify-between px-md py-sm">
        <span className="inline-flex items-center gap-sm rounded-pill bg-white/10 px-sm py-[4px] text-xs">
          <span
            aria-hidden="true"
            className={`h-[7px] w-[7px] rounded-full ${phase === "joined" ? "bg-success" : phase === "error" ? "bg-danger" : "bg-notify"}`}
          />
          Videomöte{label ? ` · ${label}` : ""}
          {phase === "connecting" ? " · ansluter…" : ""}
        </span>
      </div>
      {error ? (
        // An error is a state to act on, not a red line: what happened,
        // and the way back (try again = new room, same meeting).
        <div className="flex flex-1 flex-col items-center justify-center gap-md px-lg text-center">
          <span
            aria-hidden="true"
            className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10"
          >
            <VideoOff size={22} />
          </span>
          <h2 className="font-display text-xl">Kunde inte ansluta</h2>
          <p className="max-w-[32ch] text-sm text-white/70">
            {errorText(error)}
          </p>
          <div className="flex flex-wrap justify-center gap-sm">
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="rounded-pill bg-white px-md py-[8px] text-sm font-semibold text-brand-deep hover:opacity-90"
              >
                Försök igen
              </button>
            ) : null}
            {chatHref ? (
              <Link
                to={chatHref}
                className="rounded-pill bg-white/15 px-md py-[8px] text-sm font-semibold text-white hover:bg-white/25"
              >
                Öppna chatten
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
      <div
        ref={containerRef}
        className={`min-h-0 flex-1 ${error ? "hidden" : ""}`}
      />
    </div>
  );
}

// TES-1040 — hidden in the admin's read-only support view: joining the video call is out of scope for the support views (the
// video token is not opened to an admin).
export const MeetingRoom = hiddenWhenReadOnly(MeetingRoomCall);
